import Item from './item';
import CreateSessionInlineFormWrapper from './actions/add-session/create-session-inline-form';
import React, { Fragment } from 'react';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { Mode } from '@/types/entity-mode';
import { Box } from '@mui/material';
import { SlotInlineDetailsBelowRow } from './slot-inline-details-below-row';
import { flowControlInlinePerformMiddleWrapperSx } from '../flow-control-perform-expand';
import type { ScheduleInlineDetailsTab } from '../edit-details/schedule-inline-details-tab';
import { FLOW_CONTROL_SLOT_LIST_GAP, type FlowDetailMode } from '../flow-detail.types';
import type { VirtualInBetweenPlayout } from '@/helpers/flow-control-in-between-playout.helper';
import { isFlowControlSlotMediaBelowVisible } from '@/helpers/flow-control-playing-segment.helper';
import { FlowControlVirtualInBetweenPlayoutRow } from './flow-control-virtual-in-between-playout-row';
import { FlowControlInlineSlotMediaPanel } from './flow-control-inline-slot-media-panel';
import { useDroppable } from '@dnd-kit/core';

type DroppableContainerProps = {
  id: string;
  children: React.ReactNode;
  minHeight?: number;
  listGap?: number;
};

const DroppableContainer = ({ id, children, minHeight, listGap = 1 }: DroppableContainerProps) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: listGap,
        ...(minHeight ? { minHeight } : {}),
      }}
    >
      {children}
    </Box>
  );
};

type Props = {
  mode: Mode;
  refreshing: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
  isUntimedFormActive: boolean;
  formDuration: number;
  formAvailableDuration: number;
  formSlotType: 'Session' | 'Break';
  formInsertBeforeSlotId?: string;
  handleFormSuccess: () => void;
  handleFormClose: () => void;
  handleFormDurationChange: (duration: number) => void;
  handleShowForm: (
    gapSlotIdOrPosition: string,
    offset: number,
    slotType?: 'Session' | 'Break',
    insertBeforeSlotId?: string,
    insertAfterSlotId?: string,
  ) => void;
  untimedSlots: FlowSlotDto[];
  isSmallScreen: boolean;
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
  setSelectedSlot: (slot: FlowSlotDto | undefined) => void;
  selectedSlot?: FlowSlotDto;
  expandedSlotId: string | null;
  expandedFormType: 'timing' | 'new' | null;
  handleExpandedChange: (slotId: string | null) => void;
  handleFormTypeChange: (slotId: string, formType: 'timing' | 'new' | null) => void;
  handleSlotUpdate: (slot: FlowSlotDto) => void;
  handlePreviewSlotUpdate: (slot: FlowSlotDto) => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  cancelChanges?: () => void;
  handleSetEditingTiming: (editing: boolean, slotId: string) => void;
  editingSlotId: string | null;
  editingEditSlotId: string | null;
  previewFlow?: FlowDto;
  flowEndOffset?: number;
  setShowFlowDetails: React.Dispatch<React.SetStateAction<import('../flow-detail.types').FlowDetailMode | undefined>>;
  setFlowDetailsDirection: React.Dispatch<React.SetStateAction<'up' | 'down' | undefined>>;
  containerId: string;
  disableDrop?: boolean;
  modeOverride?: import('@/types/entity-mode').Mode;
  rightSectionOnlyPublishIcon?: boolean;
  middleSectionOpensOverlayOnSmallScreen?: boolean;
  onWideScreenSlotDeletedFromList?: () => void;
  onSessionPublishSuccess?: () => void;
  flowControlInlinePerform?: boolean;
  flowControlPerformExpandedKey?: string | null;
  onFlowControlPerformExpand?: (key: string | null) => void;
  renderFlowControlPerformMiddle?: (slot: FlowSlotDto) => React.ReactNode;
  slotAssetsInlineDetailsSlotId?: string | null;
  showSlotAssetsInlineDetails?: boolean;
  onSlotAssetsInlineDetailsToggle?: (slotId: string) => void;
  onSlotAssetsInlineDetailsClose?: (slotId: string) => void;
  smallScreenInlineDetailsVariant?: 'slotAssets' | 'schedule';
  inlineScheduleDetailsTab?: ScheduleInlineDetailsTab;
  onInlineScheduleDetailsTabChange?: (tab: ScheduleInlineDetailsTab) => void;
  flowStartOffset?: number;
  eventSlotIdParam?: string | null;
  startRowMediaOpenDetailsMode?: FlowDetailMode;
  showSlotDragHandle?: boolean;
  /** While dragging across sections, avoid mounting the empty placeholder (layout thrash on mobile). */
  hideEmptyPlaceholderWhileDragging?: boolean;
  flowControlInlineMedia?: boolean;
  flowControlInlineMediaSlotId?: string | null;
  flowControlPlayingExpandId?: string | null;
  onFlowControlInlineMediaToggle?: (slotId: string) => void;
  virtualInBetweenPlayout?: VirtualInBetweenPlayout | null;
  rightSectionPlayIcon?: boolean;
};

export const UntimedSection = ({
  mode,
  refreshing,
  t,
  isUntimedFormActive,
  formDuration,
  formAvailableDuration,
  formSlotType,
  formInsertBeforeSlotId,
  handleFormSuccess,
  handleFormClose,
  handleFormDurationChange,
  handleShowForm,
  untimedSlots,
  isSmallScreen,
  onSlotSelect,
  setSelectedSlot,
  selectedSlot,
  expandedSlotId,
  expandedFormType,
  handleExpandedChange,
  handleFormTypeChange,
  handleSlotUpdate,
  handlePreviewSlotUpdate,
  onShowTimingAlert,
  onShowPublishAlert,
  cancelChanges,
  handleSetEditingTiming,
  editingSlotId,
  editingEditSlotId,
  previewFlow,
  flowEndOffset,
  setShowFlowDetails,
  containerId,
  disableDrop = false,
  modeOverride,
  rightSectionOnlyPublishIcon = false,
  middleSectionOpensOverlayOnSmallScreen = false,
  onWideScreenSlotDeletedFromList,
  onSessionPublishSuccess,
  flowControlInlinePerform = false,
  flowControlPerformExpandedKey = null,
  onFlowControlPerformExpand,
  renderFlowControlPerformMiddle,
  slotAssetsInlineDetailsSlotId = null,
  showSlotAssetsInlineDetails = false,
  onSlotAssetsInlineDetailsToggle,
  onSlotAssetsInlineDetailsClose,
  smallScreenInlineDetailsVariant,
  inlineScheduleDetailsTab,
  onInlineScheduleDetailsTabChange,
  flowStartOffset = 0,
  eventSlotIdParam,
  startRowMediaOpenDetailsMode,
  showSlotDragHandle = false,
  hideEmptyPlaceholderWhileDragging = false,
  flowControlInlineMedia = false,
  flowControlInlineMediaSlotId = null,
  flowControlPlayingExpandId = null,
  onFlowControlInlineMediaToggle,
  virtualInBetweenPlayout = null,
  rightSectionPlayIcon = false,
}: Props) => {
  if (mode === 'PERFORM') return null;

  const renderHeader = () => {
    if (mode === 'PLAN' && isUntimedFormActive) {
      return (
        <CreateSessionInlineFormWrapper
          key="form-untimed-top"
          offset={0}
          durationMinutes={formAvailableDuration}
          initialPlannedDurationMinutes={formDuration}
          onSuccess={handleFormSuccess}
          onClose={handleFormClose}
          setSelectedSlot={setSelectedSlot}
          onFormDurationChange={handleFormDurationChange}
          slotType={formSlotType}
          isUntimed={true}
          insertBeforeSlotId={formInsertBeforeSlotId}
        />
      );
    }

    return null;
  };

  const renderSlots = () => {
    const items: React.ReactNode[] = [];

    untimedSlots.forEach((slot) => {
      items.push(
        <Fragment key={slot.id}>
          <Item
            slot={slot}
            containerId={containerId}
            rightSectionOnlyPublishIcon={rightSectionOnlyPublishIcon}
            expandedSlotId={expandedSlotId}
            onExpandedChange={handleExpandedChange}
            expandedFormType={expandedSlotId === slot.id ? expandedFormType : null}
            onFormTypeChange={handleFormTypeChange}
            isSmallScreen={isSmallScreen}
            onClick={(userInitiated = true) => {
              if (onSlotSelect) {
                onSlotSelect(slot, userInitiated);
                if (isSmallScreen) {
                  requestAnimationFrame(() => {
                    const el = document.querySelector(`[data-slot-id="${slot.id}"]`);
                    if (!el) {
                      const selectors = [
                        `[data-flow-slot-id="${slot.contentType === 'Session' ? slot.content?.id : slot.id}"]`,
                        `#flow-slot-${slot.contentType === 'Session' ? slot.content?.id : slot.id}`,
                        `#slot-${slot.id}`,
                      ];
                      const foundEl = selectors
                        .map((sel) => document.querySelector(sel))
                        .find((node): node is Element => !!node);
                      if (foundEl && 'scrollIntoView' in foundEl) {
                        foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    } else if ('scrollIntoView' in el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  });
                }
              } else {
                setSelectedSlot(slot);
                if (isSmallScreen) {
                  requestAnimationFrame(() => {
                    const el = document.querySelector(`[data-slot-id="${slot.id}"]`);
                    if (!el) {
                      const selectors = [
                        `[data-flow-slot-id="${slot.contentType === 'Session' ? slot.content?.id : slot.id}"]`,
                        `#flow-slot-${slot.contentType === 'Session' ? slot.content?.id : slot.id}`,
                        `#slot-${slot.id}`,
                      ];
                      const foundEl = selectors
                        .map((sel) => document.querySelector(sel))
                        .find((node): node is Element => !!node);
                      if (foundEl && 'scrollIntoView' in foundEl) {
                        foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    } else if ('scrollIntoView' in el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  });
                }
              }
            }}
            refreshing={refreshing}
            hydrated={previewFlow !== undefined}
            selected={flowControlInlineMedia ? flowControlInlineMediaSlotId === slot.id : selectedSlot?.id === slot.id}
            onSlotUpdate={handleSlotUpdate}
            onPreviewSlotUpdate={handlePreviewSlotUpdate}
            onShowTimingAlert={onShowTimingAlert}
            onShowPublishAlert={onShowPublishAlert}
            cancelChanges={cancelChanges}
            setEditingTiming={(editing) => handleSetEditingTiming(editing, slot.id)}
            isEditingTiming={editingSlotId === slot.id}
            isEditingEdit={editingEditSlotId === slot.id}
            localSlots={untimedSlots}
            previewFlow={previewFlow}
            flowEndOffset={flowEndOffset}
            listFlowStartOffset={flowStartOffset}
            onSlotDeleted={() => {
              if (!isSmallScreen) {
                if (onWideScreenSlotDeletedFromList) {
                  onWideScreenSlotDeletedFromList();
                } else {
                  setShowFlowDetails('flowSettings');
                }
              }
            }}
            onShowForm={handleShowForm}
            isUntimed={true}
            disableDrop={disableDrop}
            modeOverride={modeOverride}
            middleSectionOpensOverlayOnSmallScreen={middleSectionOpensOverlayOnSmallScreen}
            onSessionPublishSuccess={onSessionPublishSuccess}
            flowControlInlinePerform={flowControlInlinePerform}
            flowControlPerformExpandedKey={flowControlPerformExpandedKey}
            onFlowControlPerformExpand={onFlowControlPerformExpand}
            onSlotAssetsInlineDetailsToggle={onSlotAssetsInlineDetailsToggle}
            onSlotAssetsInlineDetailsClose={onSlotAssetsInlineDetailsClose}
            slotInlineDetailsOpen={Boolean(showSlotAssetsInlineDetails && slotAssetsInlineDetailsSlotId === slot.id)}
            smallScreenInlineDetailsVariant={smallScreenInlineDetailsVariant}
            inlineScheduleDetailsTab={inlineScheduleDetailsTab}
            onInlineScheduleDetailsTabChange={onInlineScheduleDetailsTabChange}
            startRowMediaOpenDetailsMode={startRowMediaOpenDetailsMode}
            showSlotDragHandle={showSlotDragHandle}
            flowControlInlineMedia={flowControlInlineMedia}
            onFlowControlInlineMediaToggle={onFlowControlInlineMediaToggle}
            flowControlInlineMediaOpen={Boolean(
              flowControlInlineMedia &&
              isFlowControlSlotMediaBelowVisible(slot.id, flowControlInlineMediaSlotId, flowControlPlayingExpandId),
            )}
            rightSectionPlayIcon={rightSectionPlayIcon}
          />
          {flowControlInlineMedia && (
            <FlowControlInlineSlotMediaPanel
              slot={slot}
              expandId={flowControlInlineMediaSlotId}
              playingExpandId={flowControlPlayingExpandId}
            />
          )}
          {flowControlInlineMedia && virtualInBetweenPlayout?.insertAfterSlotId === slot.id && (
            <FlowControlVirtualInBetweenPlayoutRow
              virtual={virtualInBetweenPlayout}
              isExpanded={flowControlInlineMediaSlotId === virtualInBetweenPlayout.segmentId}
              onToggleExpand={() => onFlowControlInlineMediaToggle?.(virtualInBetweenPlayout.segmentId)}
            />
          )}
          {flowControlInlinePerform && flowControlPerformExpandedKey === slot.id && renderFlowControlPerformMiddle && (
            <Box sx={{ ...flowControlInlinePerformMiddleWrapperSx, mx: 0 }}>{renderFlowControlPerformMiddle(slot)}</Box>
          )}
          <SlotInlineDetailsBelowRow
            slot={slot}
            activeSlotId={slotAssetsInlineDetailsSlotId}
            show={Boolean(showSlotAssetsInlineDetails)}
            variant={smallScreenInlineDetailsVariant}
            flowStartOffset={flowStartOffset}
            eventSlotIdParam={eventSlotIdParam}
            onWideScreenSlotDeletedFromList={onWideScreenSlotDeletedFromList}
            onSessionPublishSuccess={onSessionPublishSuccess}
            stackMarginX={0}
            inlineScheduleDetailsTab={inlineScheduleDetailsTab}
            slotAssetsListViewMode={mode}
          />
        </Fragment>,
      );
    });

    if (items.length > 0) {
      return items;
    }
    return null;
  };

  const emptyUntimedDropMinHeight =
    hideEmptyPlaceholderWhileDragging && untimedSlots.length === 0 && !isUntimedFormActive ? 48 : undefined;

  return (
    <DroppableContainer
      id={containerId}
      minHeight={emptyUntimedDropMinHeight}
      listGap={flowControlInlineMedia ? FLOW_CONTROL_SLOT_LIST_GAP : 1}
    >
      {renderHeader()}
      {untimedSlots.length === 0 && !isUntimedFormActive ? (
        hideEmptyPlaceholderWhileDragging ? (
          <Box aria-hidden sx={{ minHeight: 48, flexShrink: 0 }} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              py: 1,
              textAlign: 'center',
            }}
          >
            <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {t('flows.detail.slotList.untimedEmpty')}
            </Box>
          </Box>
        )
      ) : null}
      {renderSlots()}
    </DroppableContainer>
  );
};

export default UntimedSection;
